/**
 * The pixel primitives, written once.
 *
 * These are the helpers the hand-written generators kept redefining: px() in 74
 * of them, an outline pass under three different names in 77, dot in 25, hline
 * and vline in 34. Same behaviour, one copy.
 *
 * Everything here works on whole pixels. There is no path that can produce a
 * blended edge, because a soft edge in a 32x32 sprite is a bug every time.
 */

import type { Bitmap } from "./png.js";

export type Rgba = readonly [number, number, number, number];

export const TRANSPARENT: Rgba = [0, 0, 0, 0];

/**
 * Which edges join up to their opposite edge.
 *
 * Set on a canvas rather than on a shape, because tiling is a property of the
 * surface being drawn on and not of any one thing drawn on it. Every shape in
 * the vocabulary reaches pixels through `set`, so wrapping there is what makes
 * all eighteen of them tileable without any of them knowing.
 */
export interface Wrap {
	x: boolean;
	y: boolean;
}

export const NO_WRAP: Wrap = { x: false, y: false };

export class Canvas {
	readonly width: number;
	readonly height: number;
	readonly data: Uint8Array;
	readonly wrap: Wrap;

	constructor(width: number, height: number, wrap: Wrap = NO_WRAP) {
		this.width = width;
		this.height = height;
		this.wrap = wrap;
		this.data = new Uint8Array(width * height * 4);
	}

	/**
	 * A coordinate brought into range, or -1 if it does not belong on this canvas.
	 *
	 * Remainder twice, because a negative x in JavaScript gives a negative
	 * remainder and a shape drawn a little off the left edge has to land on the
	 * right one rather than being dropped.
	 */
	private atX(x: number): number {
		if (!this.wrap.x) return x < 0 || x >= this.width ? -1 : x;
		return ((x % this.width) + this.width) % this.width;
	}

	private atY(y: number): number {
		if (!this.wrap.y) return y < 0 || y >= this.height ? -1 : y;
		return ((y % this.height) + this.height) % this.height;
	}

	get(x: number, y: number): Rgba {
		const px = this.atX(Math.round(x));
		const py = this.atY(Math.round(y));
		if (px < 0 || py < 0) return TRANSPARENT;
		const i = (py * this.width + px) * 4;
		return [this.data[i], this.data[i + 1], this.data[i + 2], this.data[i + 3]];
	}

	/**
	 * The colour's own alpha decides what happens, not the shape drawing it.
	 *
	 * Opaque replaces the pixel, which is what PIL's rectangle fill does and
	 * therefore what the existing art was drawn with. Anything part-transparent
	 * blends over what is underneath, whichever shape asked for it.
	 *
	 * This used to need a flag that only the `glass` shape passed, and `glass`
	 * is a rectangle. So a round fish bowl was drawn with `disc`, which replaced
	 * every pixel it covered, and the flower inside it vanished. Putting the
	 * decision in the palette fixes that everywhere at once and matches how the
	 * rest of the format works: parts name a role, the kit decides what it means,
	 * and "slightly see-through" is a property of a colour rather than of a
	 * shape.
	 *
	 * A pixel is still either fully painted or not: a flat translucent fill over
	 * an opaque layer resolves to one solid colour per pixel, so this cannot
	 * produce the soft edges that ruin pixel art.
	 */
	set(x: number, y: number, c: Rgba): void {
		x = this.atX(Math.round(x));
		y = this.atY(Math.round(y));
		if (x < 0 || y < 0) return;
		const i = (y * this.width + x) * 4;
		if (c[3] === 255) {
			this.data[i] = c[0];
			this.data[i + 1] = c[1];
			this.data[i + 2] = c[2];
			this.data[i + 3] = c[3];
			return;
		}
		if (c[3] === 0) return;
		const a = c[3] / 255;
		const inv = 1 - a;
		this.data[i] = Math.round(c[0] * a + this.data[i] * inv);
		this.data[i + 1] = Math.round(c[1] * a + this.data[i + 1] * inv);
		this.data[i + 2] = Math.round(c[2] * a + this.data[i + 2] * inv);
		this.data[i + 3] = Math.max(this.data[i + 3], c[3]);
	}

	rect(x: number, y: number, w: number, h: number, c: Rgba): void {
		if (w <= 0 || h <= 0) return;
		const x0 = Math.round(x);
		const y0 = Math.round(y);
		for (let dy = 0; dy < Math.round(h); dy++) {
			for (let dx = 0; dx < Math.round(w); dx++) this.set(x0 + dx, y0 + dy, c);
		}
	}

	/** Unfilled rectangle, one pixel thick, drawn on the given bounds inclusive. */
	frame(x0: number, y0: number, x1: number, y1: number, c: Rgba): void {
		this.hline(x0, y0, x1 - x0 + 1, c);
		this.hline(x0, y1, x1 - x0 + 1, c);
		this.vline(x0, y0, y1 - y0 + 1, c);
		this.vline(x1, y0, y1 - y0 + 1, c);
	}

	hline(x: number, y: number, w: number, c: Rgba): void {
		this.rect(x, y, w, 1, c);
	}

	vline(x: number, y: number, h: number, c: Rgba): void {
		this.rect(x, y, 1, h, c);
	}

	dot(x: number, y: number, c: Rgba): void {
		this.set(x, y, c);
	}

	/** Bresenham, so a diagonal is a staircase of whole pixels. */
	line(x0: number, y0: number, x1: number, y1: number, c: Rgba): void {
		let x = Math.round(x0);
		let y = Math.round(y0);
		const ex = Math.round(x1);
		const ey = Math.round(y1);
		const dx = Math.abs(ex - x);
		const dy = -Math.abs(ey - y);
		const sx = x < ex ? 1 : -1;
		const sy = y < ey ? 1 : -1;
		let err = dx + dy;
		for (;;) {
			this.set(x, y, c);
			if (x === ex && y === ey) return;
			const e2 = 2 * err;
			if (e2 >= dy) {
				err += dy;
				x += sx;
			}
			if (e2 <= dx) {
				err += dx;
				y += sy;
			}
		}
	}

	/**
	 * The dark keyline every sprite in this style has. Any transparent pixel
	 * orthogonally touching a solid one becomes the outline colour.
	 *
	 * Computed against a snapshot, so a pixel the pass just wrote cannot seed
	 * more outline and grow the border to two pixels.
	 */
	outline(c: Rgba, threshold = 20): void {
		const solid = new Uint8Array(this.width * this.height);
		for (let i = 0; i < solid.length; i++) solid[i] = this.data[i * 4 + 3] > threshold ? 1 : 0;

		/*
		 * The neighbour lookup wraps too, and that is what removes the grid.
		 *
		 * A tile that covers its canvas edge to edge has no transparent pixel at
		 * its border, so on a wrapped axis nothing there is ever seeded and no
		 * keyline is drawn. The seam disappears as a consequence of the surface
		 * joining up, rather than needing a rule that says "no outline on this
		 * side", which would be a second thing to get wrong.
		 */
		const at = (x: number, y: number): number => {
			const px = this.atX(x);
			const py = this.atY(y);
			return px < 0 || py < 0 ? 0 : solid[py * this.width + px];
		};

		for (let y = 0; y < this.height; y++) {
			for (let x = 0; x < this.width; x++) {
				if (solid[y * this.width + x]) continue;
				if (at(x - 1, y) || at(x + 1, y) || at(x, y - 1) || at(x, y + 1)) this.set(x, y, c);
			}
		}
	}

	toBitmap(): Bitmap {
		return { width: this.width, height: this.height, data: this.data };
	}
}

export function shade(c: Rgba, amount: number): Rgba {
	const f = (v: number) => Math.max(0, Math.min(255, Math.round(v + (amount < 0 ? v : 255 - v) * amount)));
	return [f(c[0]), f(c[1]), f(c[2]), c[3]];
}

export function mix(a: Rgba, b: Rgba, t: number): Rgba {
	const f = (x: number, y: number) => Math.round(x + (y - x) * t);
	return [f(a[0], b[0]), f(a[1], b[1]), f(a[2], b[2]), f(a[3], b[3])];
}

/**
 * The largest whole-number scale a sprite fits at inside a box.
 *
 * The one rule this project argues for everywhere else, written down once. A
 * fractional scale on pixel art is the thing that must never happen, and until
 * now four places worked that out for themselves: the workbench zoom, the
 * overlay that shows a sprite being drawn, the welcome page's scenes, and the
 * YouTube thumbnails. They agreed, which is luck rather than design.
 *
 * The box may be a rectangle or, given one number, a square. `cap` is for the
 * callers that have a ceiling of their own, like a zoom control that stops at
 * sixteen.
 */
export const fitScale = (
	width: number,
	height: number,
	boxWidth: number,
	boxHeight = boxWidth,
	cap = Number.POSITIVE_INFINITY,
): number => {
	if (!width || !height) return 1;
	return Math.max(1, Math.min(cap, Math.floor(Math.min(boxWidth / width, boxHeight / height))));
};

/**
 * The same canvas, seen a few pixels along.
 *
 * Every shape draws through `set`, so moving a part is a matter of moving what
 * `set` means rather than teaching nine shapes about offsets. The view keeps the
 * real canvas's size and edges, which is the point: an inset still measures from
 * the canvas edge, and only the pixels move.
 *
 * Anything pushed past an edge is dropped by the real canvas, the same as a shape
 * drawn off the edge in the first place.
 */
export function shifted(canvas: Canvas, dx: number, dy: number): Canvas {
	if (dx === 0 && dy === 0) return canvas;

	const view = Object.create(canvas) as Canvas;
	Object.defineProperties(view, {
		set: { value: (x: number, y: number, c: Rgba) => canvas.set(x + dx, y + dy, c) },
		get: { value: (x: number, y: number) => canvas.get(x + dx, y + dy) },
	});
	return view;
}
